import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TrendingUp, ShieldCheck, Bell } from "lucide-react";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import PasswordInput from "@/components/PasswordInput";
import SMSConsentBlock from "@/components/SMSConsentBlock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ loading: true, email: null, name: null, error: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [method, setMethod] = useState("email");
  const [events, setEvents] = useState({ new_ideas: false, open_close: false, all_updates: true });
  const [autoFollow, setAutoFollow] = useState(true);
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  // Guards against React state lag on rapid double-clicks — a stale second click
  // would previously fire a duplicate accept-invite POST that 400'd because the
  // first success had already consumed the invite token.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, error: "Missing invite token" }); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/invite/${token}`);
        setState({ loading: false, email: data.email, name: data.name, error: null });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Invalid or expired invite" });
      }
    })();
  }, [token]);

  const smsRequired = method === "sms" || method === "both";

  const submit = async () => {
    if (submittingRef.current) return;
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (smsRequired && !smsConsent) { toast.error("Please agree to receive text messages, or switch to Email/Off"); return; }
    if (smsRequired && !phone.trim()) { toast.error("Please enter a mobile phone number to receive texts"); return; }
    submittingRef.current = true;
    setBusy(true);
    let acceptedName = null;
    const payload = {
      token,
      password,
      phone: phone.trim() || null,
      notification_preferences: {
        method,
        events,
        auto_follow_new_ideas: autoFollow,
        sms_consent: smsRequired ? smsConsent : false,
      },
    };
    try {
      // Do NOT send withCredentials on this cross-origin POST: the preview CDN
      // returns `Access-Control-Allow-Origin: *`, which browsers reject when
      // credentials are attached. We store the returned JWT in localStorage and
      // rely on the Bearer header for all subsequent calls, so cookies are
      // unnecessary for this handshake.
      const { data } = await axios.post(`${API}/auth/accept-invite`, payload);
      if (data.token) localStorage.setItem("token", data.token);
      acceptedName = data.name || state.email;
    } catch (e) {
      const status = e?.response?.status;
      const raw = e?.response?.data?.detail;
      const detail = typeof raw === "string" ? raw : (Array.isArray(raw) ? (raw[0]?.msg || "") : "");
      // If the invite has already been consumed (e.g. this tab is a stale duplicate
      // of a successful submission in another tab, or the user reloaded after
      // successful setup), the account is already active — send them to login
      // gracefully instead of a scary "Failed to accept invite" red toast.
      if (status === 400 && /already been used|superseded|already/i.test(detail || "")) {
        toast.success("Your account is already active. Please sign in.");
        submittingRef.current = false;
        try { nav("/login", { replace: true }); } catch { /* ignore */ }
        return;
      }
      toast.error(detail || `Failed to accept invite${status ? ` (HTTP ${status})` : " (network)"}`);
      setBusy(false);
      submittingRef.current = false;
      return;
    }
    // Invite is accepted from here on — do not surface an error toast if
    // refresh() or navigation fails; just fall back to sending the user to login.
    toast.success(`Welcome, ${acceptedName}!`);
    try { await refresh?.(); } catch { /* ignore */ }
    try { nav("/journal", { replace: true }); } catch { nav("/login", { replace: true }); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg border border-zinc-900 bg-[#0a0a0a] rounded-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-6 h-6 text-blue-500" />
          <span className="font-heading font-semibold text-xl tracking-tight">ZigZagX</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono pl-8 mb-6">Trade with confluence</div>

        {state.loading && <div className="text-sm text-zinc-500">Validating invite…</div>}

        {state.error && (
          <div className="border border-rose-500/30 bg-rose-500/5 rounded-sm p-4 text-sm text-rose-400">
            {state.error}
            <div className="mt-3">
              <Button onClick={() => nav("/login")} className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-200 h-8">Back to login</Button>
            </div>
          </div>
        )}

        {!state.loading && !state.error && (
          <>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase text-emerald-400 mb-4">
              <ShieldCheck className="w-4 h-4" /> Invite valid
            </div>
            <h1 className="font-heading text-2xl font-semibold mb-1">Welcome{state.name ? `, ${state.name}` : ""}</h1>
            <p className="text-sm text-zinc-500 mb-6">Finish setting up your account for <span className="text-zinc-300 font-mono">{state.email}</span>.</p>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Mobile Phone (optional, for text alerts)</Label>
                  <div className="mt-1">
                    <PhoneInput value={phone} onChange={setPhone} testIdPrefix="invite-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">New Password</Label>
                    <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" className="bg-zinc-950 border-zinc-800 rounded-sm h-10 font-mono mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Confirm</Label>
                    <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm" className="bg-zinc-950 border-zinc-800 rounded-sm h-10 font-mono mt-1" />
                  </div>
                </div>
                {(() => {
                  const tooShort = password.length > 0 && password.length < 8;
                  const mismatch = password.length > 0 && confirm.length > 0 && password !== confirm;
                  const match = password.length >= 8 && confirm.length > 0 && password === confirm;
                  if (tooShort) return <p className="text-[11px] text-amber-400 font-mono" data-testid="invite-pw-hint">Password must be at least 8 characters.</p>;
                  if (mismatch) return <p className="text-[11px] text-rose-400 font-mono" data-testid="invite-pw-hint">Passwords do not match.</p>;
                  if (match) return <p className="text-[11px] text-emerald-400 font-mono" data-testid="invite-pw-hint">Passwords match.</p>;
                  return null;
                })()}
              </div>

              <div className="border border-zinc-900 bg-[#080808] rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-blue-400" />
                  <span className="font-heading text-sm uppercase tracking-wider text-zinc-300">Trade Notifications</span>
                </div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 block">Notification Method</Label>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="invite-method-group">
                  {[
                    { v: "email", label: "Email" },
                    { v: "sms", label: "Text" },
                    { v: "both", label: "Email + Text" },
                    { v: "none", label: "Off" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => setMethod(opt.v)}
                      data-testid={`invite-method-${opt.v}`}
                      className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors ${
                        method === opt.v
                          ? "border-blue-500/50 bg-blue-500/10 text-blue-400"
                          : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
                {smsRequired && (
                  <SMSConsentBlock checked={smsConsent} onCheckedChange={setSmsConsent} testIdPrefix="invite-sms-consent" />
                )}
                <label className="flex items-start gap-3 p-2 mb-3 mt-3 rounded-sm border border-zinc-900 hover:border-zinc-800 cursor-pointer" data-testid="invite-auto-follow">
                  <Switch checked={autoFollow} onCheckedChange={setAutoFollow} data-testid="invite-auto-follow-switch" />
                  <div>
                    <div className="text-sm text-zinc-200">Automatically follow all New Trade Ideas</div>
                    <div className="text-[11px] text-zinc-500">You'll get an alert whenever a new trade is added.</div>
                  </div>
                </label>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 block">Notify me about</Label>
                <div className="space-y-1.5" data-testid="invite-events-group">
                  {[
                    { key: "new_ideas", label: "New Trade Ideas" },
                    { key: "open_close", label: "Trade Open / Close" },
                    { key: "all_updates", label: "All Trade Updates" },
                  ].map((opt) => {
                    const isAll = opt.key === "all_updates";
                    const disabledByAll = !isAll && !!events.all_updates;
                    const checked = isAll ? !!events.all_updates : (!!events.all_updates || !!events[opt.key]);
                    return (
                      <label key={opt.key} className={`flex items-center gap-3 p-2 rounded-sm border border-zinc-900 ${disabledByAll ? "opacity-50" : "hover:border-zinc-800 cursor-pointer"}`}>
                        <Switch checked={checked} disabled={disabledByAll} onCheckedChange={() => setEvents((e) => ({ ...e, [opt.key]: !e[opt.key] }))} data-testid={`invite-event-${opt.key}-switch`} />
                        <span className="text-sm text-zinc-200">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <Button onClick={submit} disabled={busy || password.length < 8 || password !== confirm || (smsRequired && !smsConsent)} data-testid="invite-submit" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-10 mt-2 disabled:opacity-50">
                {busy ? "Setting up…" : "Activate account"}
              </Button>
              <p className="text-[11px] text-zinc-500 text-center leading-relaxed" data-testid="invite-tos-notice">
                By continuing, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline" data-testid="invite-tos-link">
                  Terms of Service
                </a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline" data-testid="invite-privacy-link">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
